import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, BadgeVariant } from '../../components/Badge';
import { Button } from '../../components/Button';
import { Select } from '../../components/forms/Select';
import { useBookingList, useResources } from '../Calendars/scheduling/hooks';
import {
  BookingListQuery,
  BookingSource,
  BookingStatus,
  BookingSyncStatus,
  SchedulingProfile,
} from '../Calendars/scheduling/types';

const PAGE_SIZE = 20;

const STATUS_VARIANT: Record<BookingStatus, BadgeVariant> = {
  confirmed: 'success',
  cancelled: 'secondary',
  no_show: 'warning',
};

const SYNC_VARIANT: Record<BookingSyncStatus, BadgeVariant> = {
  not_required: 'secondary',
  pending: 'info',
  synced: 'success',
  failed: 'error',
};

const STATUSES: BookingStatus[] = ['confirmed', 'cancelled', 'no_show'];
const SOURCES: BookingSource[] = ['phone_agent', 'website', 'manual'];
const SYNC_STATUSES: BookingSyncStatus[] = ['synced', 'pending', 'failed'];
const ORDERINGS: BookingListQuery['ordering'][] = ['-created', 'start_datetime', '-start_datetime'];
const COLUMNS = [
  'customer',
  'service',
  'resource',
  'appointment',
  'source',
  'status',
  'created',
  'sync',
] as const;

// Activity feed of a scheduling calendar: what was booked last, by whom, and whether it synced.
export const BookingsTab: React.FC<{ profile: SchedulingProfile }> = ({ profile }) => {
  const { t, i18n } = useTranslation();
  const { data: resources = [] } = useResources(profile.id);

  const [page, setPage] = useState(1);
  const [ordering, setOrdering] = useState<BookingListQuery['ordering']>('-created');
  const [resource, setResource] = useState('');
  const [status, setStatus] = useState('');
  const [source, setSource] = useState('');
  const [syncStatus, setSyncStatus] = useState('');

  const { data, isLoading, isError } = useBookingList({
    profile: profile.id,
    page,
    pageSize: PAGE_SIZE,
    ordering,
    resource: resource || undefined,
    status: (status || undefined) as BookingStatus | undefined,
    source: (source || undefined) as BookingSource | undefined,
    syncStatus: (syncStatus || undefined) as BookingSyncStatus | undefined,
  });

  const bookings = data?.results ?? [];
  const totalPages = Math.max(1, Math.ceil((data?.count ?? 0) / PAGE_SIZE));

  // Every filter change starts again on the first page.
  const filter = (setter: (value: string) => void) => (value: string | string[]) => {
    setter(value as string);
    setPage(1);
  };

  const all = { value: '', label: t('appointments.bookings.all') };
  const formatDate = (iso: string) =>
    new Date(iso).toLocaleString(i18n.language, {
      timeZone: profile.timezone,
      dateStyle: 'medium',
      timeStyle: 'short',
    });

  const field = (label: string, control: React.ReactNode) => (
    <div className="w-full sm:w-52">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}
      </label>
      {control}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        {field(
          t('appointments.bookings.sort'),
          <Select
            options={ORDERINGS.map(value => ({
              value,
              label: t(`appointments.bookings.orderings.${value}`),
            }))}
            value={ordering}
            onChange={value => {
              setOrdering(value as BookingListQuery['ordering']);
              setPage(1);
            }}
          />
        )}
        {field(
          t('appointments.bookings.resource'),
          <Select
            options={[all, ...resources.map(item => ({ value: item.id, label: item.name }))]}
            value={resource}
            onChange={filter(setResource)}
          />
        )}
        {field(
          t('appointments.bookings.status'),
          <Select
            options={[
              all,
              ...STATUSES.map(value => ({
                value,
                label: t(`appointments.bookings.statuses.${value}`),
              })),
            ]}
            value={status}
            onChange={filter(setStatus)}
          />
        )}
        {field(
          t('appointments.bookings.source'),
          <Select
            options={[
              all,
              ...SOURCES.map(value => ({
                value,
                label: t(`appointments.bookings.sources.${value}`),
              })),
            ]}
            value={source}
            onChange={filter(setSource)}
          />
        )}
        {field(
          t('appointments.bookings.sync'),
          <Select
            options={[
              all,
              ...SYNC_STATUSES.map(value => ({
                value,
                label: t(`appointments.bookings.syncStatuses.${value}`),
              })),
            ]}
            value={syncStatus}
            onChange={filter(setSyncStatus)}
          />
        )}
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500">{t('common.loading')}</p>
      ) : isError ? (
        <p className="text-sm text-red-600">{t('appointments.bookings.loadError')}</p>
      ) : bookings.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('appointments.bookings.empty')}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="min-w-full divide-y divide-gray-200 text-sm dark:divide-gray-700">
            <thead className="bg-gray-50 text-left text-xs font-semibold uppercase text-gray-500 dark:bg-gray-800 dark:text-gray-400">
              <tr>
                {COLUMNS.map(column => (
                  <th key={column} className="px-4 py-3">
                    {t(`appointments.bookings.columns.${column}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-900">
              {bookings.map(booking => (
                <tr key={booking.id} className="align-top">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900 dark:text-white">
                      {booking.customer_name}
                    </div>
                    <div className="text-gray-500 dark:text-gray-400">{booking.customer_phone}</div>
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                    {booking.service_name}
                    {booking.variant_name && (
                      <div className="text-gray-500 dark:text-gray-400">{booking.variant_name}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                    {booking.resource_name}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-gray-700 dark:text-gray-300">
                    {formatDate(booking.start_datetime)}
                  </td>
                  <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                    {t(`appointments.bookings.sources.${booking.source}`)}
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant={STATUS_VARIANT[booking.status]} size="sm">
                      {t(`appointments.bookings.statuses.${booking.status}`)}
                    </Badge>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-gray-700 dark:text-gray-300">
                    {formatDate(booking.created)}
                  </td>
                  <td className="px-4 py-3">
                    {booking.sync_status === 'not_required' ? (
                      <span className="text-gray-400">–</span>
                    ) : (
                      <Badge variant={SYNC_VARIANT[booking.sync_status]} size="sm">
                        {t(`appointments.bookings.syncStatuses.${booking.sync_status}`)}
                      </Badge>
                    )}
                    {booking.sync_status === 'failed' && booking.sync_error && (
                      <div className="mt-1 max-w-xs text-xs text-red-600 dark:text-red-400">
                        {booking.sync_error}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(data?.count ?? 0) > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('appointments.bookings.pageOf', { page, total: totalPages, count: data?.count })}
          </p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              {t('common.back')}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
            >
              {t('common.next')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
